// ABOUTME: Configuration step for the DataSourceWizard.
// ABOUTME: Allows naming the data source and selecting default catalog/schema.

import { useState } from 'react';
import { Button, Input } from '@/components/ui';

interface ConfigureStepProps {
  catalogs: string[];
  schemas: string[];
  initialConfig: {
    name: string;
    catalog: string;
    schema: string;
  };
  isLoading: boolean;
  onSubmit: (config: { name: string; catalog: string; schema: string }) => void;
  onBack: () => void;
  onCancel: () => void;
}

export function ConfigureStep({
  catalogs,
  schemas,
  initialConfig,
  isLoading,
  onSubmit,
  onBack,
  onCancel,
}: ConfigureStepProps) {
  const [name, setName] = useState(initialConfig.name);
  const [catalog, setCatalog] = useState(initialConfig.catalog || catalogs[0] || '');
  const [schema, setSchema] = useState(initialConfig.schema || schemas[0] || '');
  const [nameError, setNameError] = useState<string>();

  const isValid = name.trim() !== '' && catalog !== '' && schema !== '';

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError('Data source name is required');
      return;
    }

    onSubmit({
      name: name.trim(),
      catalog,
      schema,
    });
  };

  return (
    <div className="px-6 py-8">
      <h2 className="text-xl font-semibold text-surface-900 mb-2">
        Configure Your Data Source
      </h2>
      <p className="text-surface-600 mb-6">
        Give your data source a name and select the default catalog and schema.
      </p>

      <div className="space-y-5 mb-8">
        <div>
          <Input
            label="Data Source Name"
            placeholder="My Databricks Warehouse"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(undefined);
            }}
            error={nameError}
            helperText="A friendly name to identify this data source"
          />
        </div>

        <div>
          <label
            htmlFor="catalog-select"
            className="block text-sm font-medium text-surface-600 mb-1.5"
          >
            Default Catalog
          </label>
          <select
            id="catalog-select"
            aria-label="Default Catalog"
            value={catalog}
            onChange={(e) => setCatalog(e.target.value)}
            className="input-base w-full"
          >
            {catalogs.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-surface-400">
            The catalog used by default for queries
          </p>
        </div>

        <div>
          <label
            htmlFor="schema-select"
            className="block text-sm font-medium text-surface-600 mb-1.5"
          >
            Default Schema
          </label>
          <select
            id="schema-select"
            aria-label="Default Schema"
            value={schema}
            onChange={(e) => setSchema(e.target.value)}
            className="input-base w-full"
          >
            {schemas.map((sch) => (
              <option key={sch} value={sch}>
                {sch}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-surface-400">
            The schema used by default for queries
          </p>
        </div>
      </div>

      <div className="bg-surface-50 rounded-lg p-4 mb-8">
        <h3 className="text-sm font-medium text-surface-700 mb-2">
          What happens next
        </h3>
        <p className="text-sm text-surface-600">
          After creating the data source, you can start investigating your data
          with natural language queries. Oleum BI will use the selected catalog
          and schema as defaults, but you can access any data you have permissions for.
        </p>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid} isLoading={isLoading}>
            Create Data Source
          </Button>
        </div>
      </div>
    </div>
  );
}
